import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatListOption } from '@angular/material/list';
import { ActivatedRoute, Router } from '@angular/router';
import { CompareGroupMemberListDTO, SelfCompareGroupMemberDTO } from '../compare-group-member.dto';
import { Logger } from '../../shared/logger/logger.component';
import { DisplayedColumn } from '../displayed-column';
import { CustomOktaAuthService } from '../../auth/oktaauth.service';
import { BatchDTO } from '../batch.dto';
import { SelfCompareGroupService } from '../selfcomparegroupservice.service';

@Component({
    selector: 'app-selfcomparegroupdetail',
    templateUrl: './selfcomparegroupdetail.component.html',
    styleUrls: ['./selfcomparegroupdetail.component.scss'],
    standalone: false
})
export class SelfCompareGroupDetailComponent implements OnInit {

  compareGroupDescription: string;

  isLoading = true;
  isListEmpty = true;
  isDirty = false;
  compareGroupId: number;
  organizationId: number;
  allSelected: boolean;
  noNameGiven = false;

  isEditing = false;
  showColumnList = false;
  showSelected = false;

  lastFilter: string;

  batchList: BatchDTO[] = [];
  compareGroupMemberList: SelfCompareGroupMemberDTO[] = [];

  displayedColumns: DisplayedColumn[] = [
    { def: 'compareGroupMember', label: 'Selected Cohorts', show: true },
    { def: 'batchName', label: 'Cohort Date', show: true },
  ];

  displayedColumnsDescription: string[] = [
    'compareGroupMember-description'
  ];

  updateColumnSelection: string[] = [];

  paginationLength = 100;
  paginationSize = 10;
  paginationSizeOptions: number[] = [5, 10, 25, 50, 100];

  showAlert = false;
  alertIsConfirm = false;
  alertMessage = '';

  dataSource: MatTableDataSource<BatchDTO> = new MatTableDataSource([]);

  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  constructor(
    private selfCompareGroupService: SelfCompareGroupService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private logger: Logger,
    private oktaService: CustomOktaAuthService
  ) { }

  ngOnInit() {
    this.oktaService.refreshToken('compare group detail init');
    this.activatedRoute.params.subscribe(params => {
      let invalid = false;
      let message = '';

      if (params.orgId === undefined || isNaN(params.orgId)) {
        invalid = true;
        message = 'Invalid organization id.';
      } else {
        this.organizationId = parseInt(params.orgId, 10);
      }

      if (params.groupId !== undefined) {
        if (isNaN(params.groupId) || params.groupId < 0) {
          invalid = true;
          message = 'Invalid group id.';
        }

        this.compareGroupId = parseInt(params.groupId, 10);
        this.isEditing = true;

      } else {
        this.compareGroupId = 0;
        this.isEditing = false;
      }

      if (invalid) {
        this.logger.log(message);
        this.router.navigate(['/comparegroups']);
      }

      let userId;

      try {
        const userData = JSON.parse(sessionStorage.getItem('okta-token-storage'));
        userId = userData.idToken.claims.user_guid;
      } catch {
        console.error('Invalid user data');
        userId = 'local';
      }

      this.getCompareGroupDetail();
    });
  }

  getCompareGroupDetail() {
    this.isLoading = true;
    const orgListSubscription =
      this.selfCompareGroupService.getBatchList(this.organizationId)
        .subscribe((result) => {
          this.batchList = result;

          if (this.compareGroupId > 0) {
            const compareGroupMemberSubscription =
              this.selfCompareGroupService.getSelfCompareGroupMembers(this.compareGroupId)
                .subscribe((members) => {
                  this.compareGroupMemberList = members;

                  this.compareGroupMemberList.forEach(member => {

                    if (!this.compareGroupDescription) {
                      this.compareGroupDescription = member.compareGroupDesc;
                    }

                    const groupId = member.compareGroupId;
                    const memberId = member.memberBatchId;

                    if (groupId === this.compareGroupId) {
                      this.batchList.forEach(l => {
                        if (memberId === l.batchId) {
                          l.compareGroupMember = true;
                        }
                      });
                    }
                  });

                  this.isLoading = false;
                  compareGroupMemberSubscription.unsubscribe();
                }
            );
          } else {
            this.isLoading = false;
          }

          if (this.batchList != null) {
            this.isListEmpty = this.batchList.length === 0;
          }

          this.dataSource = new MatTableDataSource(this.batchList);

          this.sortOrganizationList();

          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;

          this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
              case 'batchName': return new Date(item.batchName);
              default: return item[property];
            }
          };

          orgListSubscription.unsubscribe();
        }
    );
  }

  sortOrganizationList() {
    this.dataSource.data.sort((a, b) => new Date(b.batchName).getTime() - new Date(a.batchName).getTime());
  }

  getDisplayedColumns() {
    return this.displayedColumns.filter(dc => dc.show).map(dc => dc.def);
  }

  getDisplayedColumnsDescription() {
    return this.displayedColumnsDescription;
  }

  getSelectedMembers() {
    return this.dataSource.filteredData.filter(ds => ds.compareGroupMember);
  }

  removeUnselected(toKeep: MatListOption[]) {

    this.dataSource.filteredData.forEach(
      d => {
        d.compareGroupMember = false;
      }
    );

    this.dataSource.filteredData.forEach(
      d => {
        toKeep.forEach(r => {
          if (d.batchId.toString() === r.value) {
            d.compareGroupMember = true;
          }
        });
      }
    );

    this.updateGroupMembers();
  }

  onSelectAll(event) {
    event.stopPropagation();

    this.allSelected = !this.allSelected;

    this.batchList.forEach(c => {
      c.compareGroupMember = this.allSelected;
    });

    this.updateGroupMembers();
  }

  updateGroupMembers() {
    this.compareGroupMemberList = [] as SelfCompareGroupMemberDTO[];
    this.batchList.forEach(o => {
      if (o.compareGroupMember) {

        const newMember = {} as SelfCompareGroupMemberDTO;
        newMember.compareGroupId = this.compareGroupId;
        newMember.memberBatchId = o.batchId;

        this.compareGroupMemberList.push(newMember);
      }
    });

    this.isDirty = true;
  }

  applyFilter(filterValue: string) {

    this.lastFilter = filterValue;

    this.dataSource.filterPredicate = (dto: BatchDTO, filter: string) => {

      let found = false;
      this.displayedColumns.forEach(
        ds => {

          if (ds.show === true) {
            const name = ds.def;

            let dtoValue = dto[name];
            let valid = true;

            switch (typeof(dtoValue)) {
              case 'string':
                  break;
              case 'number':
                  dtoValue = dtoValue.toString();
                  break;
              default:
                  valid = false;
                  break;
            }

            if (valid) {
              found = found || dtoValue.trim().toLowerCase().indexOf(filter.trim().toLowerCase()) >= 0;
            }
          }
        }
      );

      return found;
    };

    this.dataSource.filter = filterValue;
  }

  onSave(newGroup: boolean) {

    if (this.compareGroupDescription === undefined || this.compareGroupDescription === '') {
      this.showAlert = true;
      this.alertIsConfirm = false;
      this.alertMessage = 'Please name this benchmark.';
      return;
    }

    console.log('need to find minimum members amout before saving');
    const saveList = this.createCompareGroupSaveList(newGroup);
    if (saveList.compareGroupMembers.length < 1) {
      this.showAlert = true;
      this.alertIsConfirm = false;
      this.alertMessage = 'At least 1 cohort must be selected.';
      return;
    }

    console.log('implement compare group saving', saveList)
    this.selfCompareGroupService.updateCompareGroup(saveList).subscribe(
      result => {

        this.showAlert = true;
        this.alertIsConfirm = false;
        if (result.success === true) {
          this.alertMessage = 'The Self Compare Group was saved successfully.';
          this.onReturn(false);
        } else {
          this.alertMessage = 'There was an error when saving the Self Compare Group: ' + result.message;
        }
      }
    );
  }

  closeAlert() {
    this.showAlert = false;
  }

  onCompareGroupInputFocus() {
    this.noNameGiven = false;
  }

  rowToName(row: string) {
    return this.displayedColumns.filter(dc => dc.def === row).map(dc => dc.label);
  }

  alertConfirm(event) {
    if (event) {
      this.onReturn(false);
    }
  }

  onReturn(needConfirm: boolean = true) {

    if (this.isDirty === false) {
      needConfirm = false;
    }

    if (needConfirm) {
      this.showAlert = true;
      this.alertIsConfirm = true;
      this.alertMessage = 'Are you sure you want to leave this page?\nAll unsaved changes will be lost.';
    } else {
      this.router.navigate([`/selfcomparegroups/${this.organizationId}`]);
    }
  }

  createCompareGroupSaveList(newGroup: boolean): CompareGroupMemberListDTO {

    const members: number[] = [];

    this.compareGroupMemberList.forEach(
      m => {
        members.push(m.memberBatchId);
      }
    );

    let id = this.compareGroupId;
    if (newGroup || this.compareGroupId === undefined) {
      id = 0;
    }

    const list: CompareGroupMemberListDTO = {
      compareGroupDesc: this.compareGroupDescription,
      compareGroupId: id,
      orgId: this.organizationId,
      compareGroupMembers: members
    };

    return list;
  }
}
